import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, Box, Tag, Shield, Database, MapPin, Settings as SettingsIcon,
  Upload, ArrowLeft, Plus, Save, Image as ImageIcon, Barcode, ChevronRight,
} from "lucide-react";
import { api } from "../../../lib/auth";
import { Card, SectionHeader, Field, TextInput, Select, Toggle, PrimaryButton, GhostButton, uploadProductImage } from "./_shared";

const BARCODE_OPTIONS = [
  { value: "CODE128", label: "Code 128" },
  { value: "CODE39", label: "Code 39" },
  { value: "EAN13", label: "EAN-13" },
  { value: "EAN8", label: "EAN-8" },
  { value: "UPCA", label: "UPC-A" },
];
const TYPES = [
  { value: "Standard Product", label: "Standard Product" },
  { value: "Variable", label: "Variable" },
  { value: "Service", label: "Service" },
  { value: "Combo", label: "Combo" },
];

const EMPTY = {
  name: "", code: "", barcode_symbology: "CODE128", image_url: "", description: "",
  category_id: "", subcategory_id: "", brand_id: "",
  type: "Standard Product", product_unit_id: "", sale_unit_id: "", purchase_unit_id: "",
  stock_alert: 0, weight: 0, length: 0, width: 0, height: 0,
  cost: 0, price: 0, wholesale_price: 0, min_sell_price: 0,
  order_tax: 0, tax_type: "exclusive", discount_type: "percent", discount: 0, points: 0,
  warranty_months: 0, has_guarantee: false, warranty_terms: "",
  opening_stock: 0, opening_warehouse_id: "", internal_location: "",
  has_imei: false, not_for_selling: false, active: true, featured: false, hide_from_online_store: false,
};

function QuickCreate({ open, label, onClose, onCreated, endpoint }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const r = await api.post(endpoint, { name, active: true });
      toast.success("Created");
      onCreated(r.data);
      setName(""); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg text-black">New {label}</h3>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
          placeholder="Name" className="mt-3 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-lg border border-black/15 text-sm">Cancel</button>
          <PrimaryButton onClick={submit} disabled={busy}>{busy ? "…" : "Create"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default function ProductForm() {
  const { subdomain, productId } = useParams();
  const nav = useNavigate();
  const isEdit = !!productId;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Lookups
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [quick, setQuick] = useState(null); // {kind: "category"|"brand"|"unit"|"warehouse"}

  const upd = (patch) => setForm((p) => ({ ...p, ...patch }));

  const reloadLookups = async () => {
    const [c, s, b, u, w] = await Promise.all([
      api.get("/t/categories"), api.get("/t/subcategories"),
      api.get("/t/brands"), api.get("/t/units"), api.get("/t/warehouses"),
    ]);
    setCats(c.data); setSubs(s.data); setBrands(b.data); setUnits(u.data); setWarehouses(w.data);
  };

  useEffect(() => {
    reloadLookups();
    if (isEdit) {
      api.get(`/t/products/${productId}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => {
        toast.error("Product not found");
        nav(`/t/${subdomain}/products`);
      });
    }
  }, [productId]); // eslint-disable-line

  const filteredSubs = subs.filter((s) => !form.category_id || s.parent_id === form.category_id);

  const onImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const j = await uploadProductImage(file);
      upd({ image_url: j.url });
      toast.success("Image uploaded");
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const generateCode = () => {
    const c = "BR-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    upd({ code: c });
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Product name required");
    if (!form.category_id) return toast.error("Category required");
    if (!form.product_unit_id) return toast.error("Product unit required");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/t/products/${productId}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/t/products", form);
        toast.success("Product created");
      }
      nav(`/t/${subdomain}/products`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Unit + Sale Unit + Purchase Unit options
  const unitOpts = units.map((u) => ({ value: u.id, label: u.short_name ? `${u.name} (${u.short_name})` : u.name }));

  return (
    <div data-testid="product-form-page" className="p-6 lg:p-8 pb-32 max-w-[1400px]">
      {/* Header */}
      <div className="mb-6">
        <Link to={`/t/${subdomain}/products`} className="inline-flex items-center gap-1.5 text-sm text-black/55 hover:text-black mb-2">
          <ArrowLeft size={13} /> Back to products
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">
              {isEdit ? "Edit product" : "Create product"}
            </h1>
            <div className="text-xs text-black/55 mt-1 inline-flex items-center gap-1">
              Products <ChevronRight size={11} className="text-black/30" /> {isEdit ? "Edit" : "Create product"}
            </div>
          </div>
        </div>
      </div>

      {/* BASIC INFORMATION */}
      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={Package} title="Basic Information" />
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Field lbl="Name" required>
              <TextInput value={form.name} onChange={(v) => upd({ name: v })} placeholder="Enter product name" testid="pf-name" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field lbl="Barcode Symbology" required>
                <Select value={form.barcode_symbology} onChange={(v) => upd({ barcode_symbology: v })}
                  options={BARCODE_OPTIONS} testid="pf-barcode" />
              </Field>
              <Field lbl="Code Product" required hint="Scan barcode or generate one">
                <div className="flex gap-2">
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-black/15 bg-[#FAFBFB] flex-1">
                    <Barcode size={16} className="text-[#1FA868]" />
                    <input value={form.code} onChange={(e) => upd({ code: e.target.value })} placeholder="Enter or generate code"
                      data-testid="pf-code" className="flex-1 bg-transparent focus:outline-none text-sm font-mono" />
                  </div>
                  <button type="button" onClick={generateCode} data-testid="pf-gencode"
                    className="px-3 py-2 rounded-lg bg-[#E8F5E9] text-[#1FA868] text-xs font-bold hover:bg-[#D1F0DA]">
                    Generate
                  </button>
                </div>
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field lbl="Category" required>
                <Select value={form.category_id} onChange={(v) => upd({ category_id: v, subcategory_id: "" })}
                  options={cats.map((c) => ({ value: c.id, label: c.name }))} testid="pf-category"
                  addOn={
                    <button type="button" onClick={() => setQuick({ kind: "category", label: "Category", endpoint: "/t/categories" })}
                      data-testid="pf-category-add"
                      className="px-3 rounded-lg bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white"><Plus size={14} /></button>
                  } />
              </Field>
              <Field lbl="Sub Category">
                <Select value={form.subcategory_id} onChange={(v) => upd({ subcategory_id: v })}
                  options={filteredSubs.map((c) => ({ value: c.id, label: c.name }))} testid="pf-subcategory"
                  placeholder={form.category_id ? "Choose sub category" : "Pick category first"} />
              </Field>
            </div>

            <Field lbl="Brand">
              <Select value={form.brand_id} onChange={(v) => upd({ brand_id: v })}
                options={brands.map((c) => ({ value: c.id, label: c.name }))} testid="pf-brand"
                addOn={
                  <button type="button" onClick={() => setQuick({ kind: "brand", label: "Brand", endpoint: "/t/brands" })}
                    data-testid="pf-brand-add"
                    className="px-3 rounded-lg bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white"><Plus size={14} /></button>
                } />
            </Field>

            <Field lbl="Description">
              <textarea value={form.description} onChange={(e) => upd({ description: e.target.value })} rows={3}
                data-testid="pf-description" placeholder="A few words…"
                className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
            </Field>
          </div>

          <Field lbl="Product Image" hint="JPG/PNG/GIF, max 5MB" className="lg:col-span-1">
            <label className={`block rounded-xl border-2 border-dashed border-black/15 p-6 text-center cursor-pointer hover:border-[#1FA868] hover:bg-[#F0FAF3] transition-colors ${uploading ? "opacity-60" : ""}`}>
              {form.image_url ? (
                <div className="relative">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.image_url}`} alt="" className="max-h-48 mx-auto rounded-lg object-cover" />
                  <span className="block mt-3 text-xs text-[#1FA868] font-semibold">Click to change</span>
                </div>
              ) : (
                <div>
                  <ImageIcon size={36} className="mx-auto text-black/30" />
                  <div className="mt-2 text-sm font-semibold text-black">Drop image or click to browse</div>
                  <div className="text-[11px] text-black/45 mt-1">JPG, PNG, GIF — up to 5MB</div>
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" data-testid="pf-image"
                onChange={(e) => onImage(e.target.files?.[0])} disabled={uploading} />
            </label>
          </Field>
        </div>
      </Card>

      {/* INVENTORY */}
      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={Box} title="Inventory" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field lbl="Type" required>
            <Select value={form.type} onChange={(v) => upd({ type: v })} options={TYPES} testid="pf-type" />
          </Field>
          <Field lbl="Product Unit" required>
            <Select value={form.product_unit_id} onChange={(v) => upd({ product_unit_id: v, sale_unit_id: v, purchase_unit_id: v })}
              options={unitOpts} testid="pf-unit"
              addOn={
                <button type="button" onClick={() => setQuick({ kind: "unit", label: "Unit", endpoint: "/t/units" })}
                  data-testid="pf-unit-add"
                  className="px-3 rounded-lg bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white"><Plus size={14} /></button>
              } />
          </Field>
          <Field lbl="Sale Unit" required>
            <Select value={form.sale_unit_id} onChange={(v) => upd({ sale_unit_id: v })} options={unitOpts} testid="pf-sale-unit" />
          </Field>
          <Field lbl="Purchase Unit" required>
            <Select value={form.purchase_unit_id} onChange={(v) => upd({ purchase_unit_id: v })} options={unitOpts} testid="pf-purchase-unit" />
          </Field>
          <Field lbl="Stock Alert" hint="Get notified when stock falls below this">
            <TextInput type="number" value={form.stock_alert} onChange={(v) => upd({ stock_alert: v })} testid="pf-stock-alert" />
          </Field>
          <Field lbl="Weight (kg)">
            <TextInput type="number" value={form.weight} onChange={(v) => upd({ weight: v })} testid="pf-weight" />
          </Field>
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-black/55 mb-2">Dimensions (in)</div>
          <div className="grid grid-cols-3 gap-3">
            {[["length", "Length"], ["width", "Width"], ["height", "Height"]].map(([k, lbl]) => (
              <Field key={k} lbl={lbl}>
                <TextInput type="number" value={form[k]} onChange={(v) => upd({ [k]: v })} testid={`pf-${k}`} />
              </Field>
            ))}
          </div>
        </div>
      </Card>

      {/* PRICING & TAX */}
      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={Tag} title="Pricing and Tax" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field lbl="Product Cost" required>
            <TextInput type="number" value={form.cost} onChange={(v) => upd({ cost: v })} suffix="₦" testid="pf-cost" />
          </Field>
          <Field lbl="Retail Price" required>
            <TextInput type="number" value={form.price} onChange={(v) => upd({ price: v })} suffix="₦" testid="pf-price" />
          </Field>
          <Field lbl="Wholesale Price">
            <TextInput type="number" value={form.wholesale_price} onChange={(v) => upd({ wholesale_price: v })} suffix="₦" testid="pf-wholesale" />
          </Field>
          <Field lbl="Minimum Selling Price">
            <TextInput type="number" value={form.min_sell_price} onChange={(v) => upd({ min_sell_price: v })} suffix="₦" testid="pf-minsell" />
          </Field>
          <Field lbl="Order Tax">
            <TextInput type="number" value={form.order_tax} onChange={(v) => upd({ order_tax: v })} suffix="%" testid="pf-tax" />
          </Field>
          <Field lbl="Tax Type" required>
            <Select value={form.tax_type} onChange={(v) => upd({ tax_type: v })}
              options={[{ value: "exclusive", label: "Exclusive" }, { value: "inclusive", label: "Inclusive" }]} testid="pf-taxtype" />
          </Field>
          <Field lbl="Discount Type" required>
            <Select value={form.discount_type} onChange={(v) => upd({ discount_type: v })}
              options={[{ value: "percent", label: "Percent %" }, { value: "fixed", label: "Fixed amount" }]} testid="pf-discounttype" />
          </Field>
          <Field lbl="Discount">
            <TextInput type="number" value={form.discount} onChange={(v) => upd({ discount: v })} testid="pf-discount" />
          </Field>
          <Field lbl="Loyalty Points" hint="Earned per unit sold">
            <TextInput type="number" value={form.points} onChange={(v) => upd({ points: v })} testid="pf-points" />
          </Field>
        </div>
      </Card>

      {/* WARRANTY */}
      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={Shield} title="Warranty & Guarantee Tracking" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field lbl="Warranty Period (months)">
            <TextInput type="number" value={form.warranty_months} onChange={(v) => upd({ warranty_months: v })} testid="pf-warranty-months" />
          </Field>
          <Field lbl="Has Guarantee?">
            <Toggle value={form.has_guarantee} onChange={(v) => upd({ has_guarantee: v })} testid="pf-has-guarantee" />
          </Field>
        </div>
        <div className="mt-4">
          <Field lbl="Warranty Terms">
            <textarea value={form.warranty_terms} onChange={(e) => upd({ warranty_terms: e.target.value })} rows={3}
              data-testid="pf-warranty-terms" placeholder="Enter warranty terms…"
              className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
          </Field>
        </div>
      </Card>

      {/* OPENING STOCK */}
      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={Database} title="Opening Stock" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field lbl="Default Warehouse">
            <Select value={form.opening_warehouse_id} onChange={(v) => upd({ opening_warehouse_id: v })}
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))} testid="pf-warehouse"
              addOn={
                <button type="button" onClick={() => setQuick({ kind: "warehouse", label: "Warehouse", endpoint: "/t/warehouses" })}
                  data-testid="pf-warehouse-add"
                  className="px-3 rounded-lg bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white"><Plus size={14} /></button>
              } />
          </Field>
          <Field lbl="Opening Stock Quantity">
            <TextInput type="number" value={form.opening_stock} onChange={(v) => upd({ opening_stock: v })} testid="pf-opening-stock" />
          </Field>
        </div>
      </Card>

      {/* INTERNAL LOCATION */}
      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={MapPin} title="Internal Location (Rack/Shelf)" hint="Optional — for faster picking during inventory checks." />
        <Field lbl="Rack / Shelf / Zone / Bin">
          <TextInput value={form.internal_location} onChange={(v) => upd({ internal_location: v })} placeholder="e.g. A1-S3" testid="pf-internal-location" />
        </Field>
      </Card>

      {/* OPTIONS */}
      <Card className="p-6 lg:p-7 mb-5">
        <SectionHeader icon={SettingsIcon} title="Options" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            ["has_imei", "Has IMEI / Serial"],
            ["not_for_selling", "Not for selling"],
            ["active", "Active"],
            ["featured", "Featured"],
            ["hide_from_online_store", "Hide from online store"],
          ].map(([k, lbl]) => (
            <Toggle key={k} value={form[k]} onChange={(v) => upd({ [k]: v })} label={lbl} testid={`pf-opt-${k}`} />
          ))}
        </div>
      </Card>

      {/* Sticky save bar */}
      <div className="bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center justify-between sticky bottom-4 shadow-lg">
        <div className="text-sm text-black/55">{isEdit ? `Editing "${form.name || "—"}"` : "Fill required fields & click Submit"}</div>
        <div className="flex gap-2">
          <Link to={`/t/${subdomain}/products`}>
            <button className="px-4 py-2.5 rounded-xl border border-black/15 text-sm font-semibold">Cancel</button>
          </Link>
          <PrimaryButton onClick={submit} disabled={busy} icon={Save} testid="pf-submit">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Submit")}
          </PrimaryButton>
        </div>
      </div>

      <QuickCreate
        open={!!quick}
        label={quick?.label || ""}
        endpoint={quick?.endpoint}
        onClose={() => setQuick(null)}
        onCreated={async (newItem) => {
          await reloadLookups();
          if (quick.kind === "category") upd({ category_id: newItem.id });
          if (quick.kind === "brand") upd({ brand_id: newItem.id });
          if (quick.kind === "unit") upd({ product_unit_id: newItem.id, sale_unit_id: newItem.id, purchase_unit_id: newItem.id });
          if (quick.kind === "warehouse") upd({ opening_warehouse_id: newItem.id });
        }}
      />
    </div>
  );
}
